import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

// A removed/replaced modal must not leave the page locked or unlock another modal.
const scrollLockOwners = new Set();
let bodyScrollState;

function lockBody(owner) {
  if (scrollLockOwners.has(owner)) return;
  if (scrollLockOwners.size === 0) {
    const { width, position, top } = document.body.style;
    bodyScrollState = { width, position, top, y: window.scrollY };
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${bodyScrollState.y}px`;
  }
  scrollLockOwners.add(owner);
}

function unlockBody(owner) {
  if (!scrollLockOwners.delete(owner) || scrollLockOwners.size > 0) return;
  const { width, position, top, y } = bodyScrollState;
  Object.assign(document.body.style, { width, position, top });
  bodyScrollState = undefined;
  window.scrollTo({ top: y, behavior: 'instant' });
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    this.refs.dialog.addEventListener('cancel', this.#handleCancel);
    this.refs.dialog.addEventListener('close', this.#handleNativeClose);
    if (scrollLockOwners.has(this)) {
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    }

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('cancel', this.#handleCancel);
    this.refs.dialog.removeEventListener('close', this.#handleNativeClose);
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    // DOM reconciliation may move this element and reconnect it in the same task.
    queueMicrotask(() => {
      const { dialog } = this.refs;
      if (this.isConnected) {
        if (scrollLockOwners.has(this) && dialog.open && !dialog.matches(':modal')) {
          dialog.close();
          dialog.showModal();
        }
        return;
      }
      cancelAnimationFrame(this.#openFrame);
      this.#openFrame = 0;
      if (dialog.open) dialog.close();
      unlockBody(this);
    });
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #openFrame = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open || this.#openFrame) return;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    this.#openFrame = requestAnimationFrame(() => {
      this.#openFrame = 0;
      if (!this.isConnected) return;
      lockBody(this);
      try {
        dialog.showModal();
      } catch (error) {
        unlockBody(this);
        throw error;
      }
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;
    cancelAnimationFrame(this.#openFrame);
    this.#openFrame = 0;
    if (!dialog.open) {
      unlockBody(this);
      return;
    }

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    dialog.close();
    unlockBody(this);
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Native Escape can cancel the dialog after a content update removes the focused control.
   * Keep it on the normal close path so the body scroll lock is released.
   * @param {Event} event
   */
  #handleCancel = (event) => {
    event.preventDefault();
    if (this.refs.dialog.classList.contains('dialog-closing')) return;
    this.closeDialog();
  };

  #handleNativeClose = () => {
    if (!this.refs.dialog.open) unlockBody(this);
  };

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
